import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { AppDispatch, RootState } from "../../../store";
import backendApi, { TAGS } from "../services/backendApi";
import type {
  MealTypeEnum,
  OnboardingCookingStylesEnum,
  OnboardingMotivationEnum,
  Organisation,
  SimpleAllergensEnum,
  SimpleDietEnum,
  UserProfile,
  UserProfileRequest,
} from "../services/backendTypes";
import { userSlice } from "./userSlice";

export interface SimpleOnboardingState {
  // Step 1: Motivation
  motivation: OnboardingMotivationEnum | null;

  // Step 2: Cooking Style (max 2 selections)
  cookingStyles: OnboardingCookingStylesEnum[];

  // Step 3: Diet Preference
  diet: SimpleDietEnum | null;

  // Step 4: Allergies & Intolerances
  allergens: SimpleAllergensEnum[];

  // Step 5: Meal Times (at least 1 required)
  mealTimes: MealTypeEnum[];

  // UI State Management
  currentStep: number;
  isSubmitting: boolean;
  error: string | null;
  isCompleted: boolean;
}

const initialState: SimpleOnboardingState = {
  motivation: null,
  cookingStyles: [],
  diet: null,
  allergens: [],
  mealTimes: [],
  currentStep: 1,
  isSubmitting: false,
  error: null,
  isCompleted: false,
};

// Helper functions
export const isSimpleOrganization = (organisation: Organisation | null): boolean =>
  organisation?.recipe_system_type === "SIMPLE";

export const validateCookingStyles = (styles: OnboardingCookingStylesEnum[]): boolean =>
  styles.length > 0 && styles.length <= 2;

export const validateMealTimes = (mealTimes: MealTypeEnum[]): boolean => mealTimes.length > 0;

// Helper function to validate onboarding data
const validateOnboardingData = (onboarding: SimpleOnboardingState): { isValid: boolean; error?: string } => {
  if (!onboarding.motivation) {
    return { isValid: false, error: "Motivation is required" };
  }

  if (!validateCookingStyles(onboarding.cookingStyles)) {
    return { isValid: false, error: "Please select 1-2 cooking styles" };
  }

  if (!validateMealTimes(onboarding.mealTimes)) {
    return { isValid: false, error: "Please select at least one meal time" };
  }

  return { isValid: true };
};

// Helper function to map onboarding state to API format
const mapOnboardingToProfileData = (onboarding: SimpleOnboardingState): Partial<UserProfile> => ({
  onboarding_motivation: onboarding.motivation,
  onboarding_cooking_styles: onboarding.cookingStyles,
  simple_diet: onboarding.diet,
  simple_allergens: onboarding.allergens,
  preferred_meal_times: onboarding.mealTimes,
});

// Helper function to create default profile data for new users
const createDefaultProfileData = (onboardingData: Partial<UserProfile>, userId: number): UserProfileRequest => ({
  // Required fields with sensible defaults
  gender: "FEMALE",
  activity: "SEDENTARY",
  sustainability: "OPTIMAL",
  diet: "OMNIVORE",
  weight: 70,
  body_fat_percentage: 0.2,
  // Include user_id for the backend
  user_id: userId,
  // Add onboarding data on top of defaults
  ...onboardingData,
});

// Helper function to check if profile exists and get profile ID
const checkProfileExistsAndGetId = async (
  dispatch: AppDispatch,
  userId: number
): Promise<{ exists: boolean; profileId?: number }> => {
  // Get the user object which includes the intake (userprofile) if it exists
  const user = await dispatch(backendApi.endpoints.usersUserRetrieve.initiate({ id: userId })).unwrap();

  // Check if user has an intake (userprofile)
  if (user.intake?.id) {
    return { exists: true, profileId: user.intake.id };
  }
  return { exists: false };
};

// Helper function to create user profile
const createUserProfile = async (
  dispatch: AppDispatch,
  userId: number,
  profileData: Partial<UserProfile>
): Promise<UserProfile> => {
  // Create the profile (backend automatically links it to the user)
  const createdProfile = await dispatch(
    backendApi.endpoints.usersUserProfileCreate.initiate({
      userProfileRequest: createDefaultProfileData(profileData, userId),
    })
  ).unwrap();

  return createdProfile;
};

// Helper function to update user profile
const updateUserProfile = (
  dispatch: AppDispatch,
  profileId: number,
  profileData: Partial<UserProfile>
): Promise<UserProfile> =>
  dispatch(
    backendApi.endpoints.usersUserProfilePartialUpdate.initiate({
      id: profileId,
      patchedUserProfileRequest: profileData,
    })
  ).unwrap();

// Main async thunk - now much cleaner
export const submitSimpleOnboarding = createAsyncThunk<
  UserProfile,
  void,
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>("simpleOnboarding/submit", async (_, { getState, rejectWithValue, dispatch }) => {
  const state = getState();
  const { simpleOnboarding, user } = state;

  // Get current user
  const currentUser = user.user;
  if (!currentUser?.id) {
    return rejectWithValue("User ID not found");
  }

  // Validate data
  const validation = validateOnboardingData(simpleOnboarding as SimpleOnboardingState);
  if (!validation.isValid) {
    return rejectWithValue(validation.error || "Validation failed");
  }

  // Map data to API format
  const profileData = mapOnboardingToProfileData(simpleOnboarding as SimpleOnboardingState);

  try {
    // Check if profile exists and get profile ID
    const { exists: profileExists, profileId } = await checkProfileExistsAndGetId(dispatch, currentUser.id as number);

    // Create or update profile
    const result =
      profileExists && profileId
        ? await updateUserProfile(dispatch, profileId, profileData)
        : await createUserProfile(dispatch, currentUser.id as number, profileData);

    // Check if user has a nutrition plan
    if (!result.weekly_nutrition_plan) {
      // Create default nutrition plan
      try {
        await dispatch(
          backendApi.endpoints.usersUserProfileCreateDefaultNutritionPlanCreate.initiate({
            userProfileId: result.id,
          })
        ).unwrap();

        // Force refetch ME endpoint to ensure Redux store is updated
        // This is a belt-and-suspenders approach in addition to the onQueryStarted handler
        await dispatch(
          backendApi.endpoints.usersAuthUsersMeRetrieve.initiate(undefined, {
            forceRefetch: true,
          })
        ).unwrap();

        // Also manually invalidate tags to ensure all related queries are refreshed
        dispatch(backendApi.util.invalidateTags([{ type: TAGS.USER }]));
      } catch (nutritionPlanError) {
        console.error("Failed to create default nutrition plan:", nutritionPlanError);
        // Don't fail the whole onboarding if nutrition plan creation fails
        // The user can still use the app and create a plan later
      }
    }

    // Auto-plan 7 days of meals for SIMPLE organizations
    const org = currentUser.organisation || currentUser.coach?.organisation;
    if (org?.recipe_system_type === "SIMPLE") {
      try {
        const today = new Date();
        const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 6);

        await dispatch(
          backendApi.endpoints.plannerAutoplanSimpleRecipesCreate.initiate({
            autoPlanSimpleRecipesRequest: {
              start_date: startDate.toISOString().split("T")[0] as string,
              end_date: endDate.toISOString().split("T")[0] as string,
              delete_existing_plans: false,
            },
          })
        ).unwrap();
      } catch (autoplanError) {
        console.error("Failed to auto-plan meals:", autoplanError);
        // Don't fail onboarding if auto-planning fails
      }
    }

    // Refresh user data
    const updatedUser = await dispatch(
      backendApi.endpoints.usersUserRetrieve.initiate(
        { id: currentUser.id as number },
        {
          forceRefetch: true,
        }
      )
    ).unwrap();

    // CRITICAL: Store the updated user data in Redux store
    // This ensures DiaryViewScreen has the nutrition plan data
    dispatch(userSlice.actions.storeUser(updatedUser));

    // Also update the ME endpoint cache to keep it in sync
    dispatch(backendApi.util.updateQueryData("usersAuthUsersMeRetrieve", undefined, () => updatedUser));

    return result;
  } catch (error: unknown) {
    // Extract detailed error information for debugging
    const apiError = error as {
      data?: { detail?: string; [key: string]: unknown };
      message?: string;
      status?: number;
    };

    let errorMessage = "Failed to save onboarding data. Please try again.";

    if (apiError.data) {
      // If there's API error data, include more details
      errorMessage =
        apiError.data.detail || JSON.stringify(apiError.data) || `API Error (${apiError.status || "unknown"})`;
    } else if (apiError.message) {
      errorMessage = apiError.message;
    }

    console.error("Onboarding submission error:", error);
    return rejectWithValue(errorMessage);
  }
});

export const simpleOnboardingSlice = createSlice({
  name: "simpleOnboarding",
  initialState,
  reducers: {
    setMotivation: (state, action: PayloadAction<OnboardingMotivationEnum | null>) => {
      state.motivation = action.payload;
    },
    setCookingStyles: (state, action: PayloadAction<OnboardingCookingStylesEnum[]>) => {
      state.cookingStyles = action.payload;
    },
    setDiet: (state, action: PayloadAction<SimpleDietEnum | null>) => {
      state.diet = action.payload;
    },
    setAllergens: (state, action: PayloadAction<SimpleAllergensEnum[]>) => {
      state.allergens = action.payload;
    },
    setMealTimes: (state, action: PayloadAction<MealTypeEnum[]>) => {
      state.mealTimes = action.payload;
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    resetOnboarding: (state) => {
      Object.assign(state, initialState);
    },
    clearError: (state) => {
      state.error = null;
    },
    populateFromUserProfile: (state, action: PayloadAction<Partial<UserProfile>>) => {
      const profile = action.payload;
      state.motivation = profile.onboarding_motivation || null;
      state.cookingStyles = profile.onboarding_cooking_styles || [];
      state.diet = profile.simple_diet || null;
      state.allergens = profile.simple_allergens || [];
      state.mealTimes = profile.preferred_meal_times || [];
      state.currentStep = 1;
      state.error = null;
      state.isCompleted = false;
      state.isSubmitting = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitSimpleOnboarding.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(submitSimpleOnboarding.fulfilled, (state) => {
        state.isSubmitting = false;
        state.isCompleted = true;
        state.error = null;
      })
      .addCase(submitSimpleOnboarding.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload as string;
      });
  },
});

// Actions
export const {
  setMotivation,
  setCookingStyles,
  setDiet,
  setAllergens,
  setMealTimes,
  setCurrentStep,
  resetOnboarding,
  clearError,
  populateFromUserProfile,
} = simpleOnboardingSlice.actions;

// Basic selectors
export const selectMotivation = (state: RootState): OnboardingMotivationEnum | null =>
  state.simpleOnboarding.motivation;
export const selectCookingStyles = (state: RootState): OnboardingCookingStylesEnum[] =>
  state.simpleOnboarding.cookingStyles;
export const selectDiet = (state: RootState): SimpleDietEnum | null => state.simpleOnboarding.diet;
export const selectAllergens = (state: RootState): SimpleAllergensEnum[] => state.simpleOnboarding.allergens;
export const selectMealTimes = (state: RootState): MealTypeEnum[] => state.simpleOnboarding.mealTimes;
export const selectCurrentStep = (state: RootState): number => state.simpleOnboarding.currentStep;
export const selectIsSubmitting = (state: RootState): boolean => state.simpleOnboarding.isSubmitting;
export const selectError = (state: RootState): string | null => state.simpleOnboarding.error;
export const selectIsCompleted = (state: RootState): boolean => state.simpleOnboarding.isCompleted;

// Derived selectors
export const selectIsStepValid = createSelector(
  [selectCurrentStep, selectMotivation, selectCookingStyles, selectDiet, selectAllergens, selectMealTimes],
  (currentStep, motivation, cookingStyles, diet, allergens, mealTimes) => {
    switch (currentStep) {
      case 1: // Motivation
        return motivation !== null;
      case 2: // Cooking styles
        return validateCookingStyles(cookingStyles);
      case 3: // Diet (optional)
        return true; // Diet is optional
      case 4: // Allergens (optional)
        return true; // Allergens are optional
      case 5: // Meal times
        return validateMealTimes(mealTimes);
      default:
        return false;
    }
  }
);

export const selectCanProceedToNextStep = createSelector(
  [selectIsStepValid, selectCurrentStep],
  (isStepValid, currentStep) => isStepValid && currentStep < 5
);

export const selectIsOnboardingComplete = createSelector(
  [selectMotivation, selectCookingStyles, selectMealTimes],
  (motivation, cookingStyles, mealTimes) =>
    motivation !== null && validateCookingStyles(cookingStyles) && validateMealTimes(mealTimes)
);

export const selectOnboardingProgress = createSelector([selectCurrentStep], (currentStep) =>
  Math.min((currentStep / 5) * 100, 100)
);

export default simpleOnboardingSlice.reducer;
